import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { IDoesFilterPassParams, IFilterParams } from "@ag-grid-community/core";
import { Box, Button, Checkbox, CheckboxGroup, Flex, Text, VStack } from "@chakra-ui/react";
import { CandidateQualification, displayQualification } from "../../../../api/models/EntityCandidate";

const QualificationFilter = forwardRef((props: IFilterParams<{ qualifications: CandidateQualification[] }>, ref) => {
    const [selectedQualifications, setSelectedQualifications] = useState<string[]>([]);

    const qualificationOptions = useMemo(
        () =>
            Object.keys(CandidateQualification)
                .filter(key => isNaN(Number(key)))
                .sort((a, b) =>
                    displayQualification(CandidateQualification[a as keyof typeof CandidateQualification]).localeCompare(
                        displayQualification(CandidateQualification[b as keyof typeof CandidateQualification])
                    )
                ),
        []
    );

    useImperativeHandle(ref, () => ({
        doesFilterPass(params: IDoesFilterPassParams<{ qualifications: CandidateQualification[] }>) {
            if (selectedQualifications.length === 0) {
                return true;
            }
            return selectedQualifications.some(qualification =>
                params.data.qualifications?.includes(CandidateQualification[qualification as keyof typeof CandidateQualification])
            );
        },

        isFilterActive() {
            return selectedQualifications.length > 0;
        },

        getModel() {
            if (!this.isFilterActive()) {
                return null;
            }
            return { filterType: "set", type: "contains", values: selectedQualifications };
        },

        setModel(model: any) {
            const values = model == null ? [] : model.values || [];
            setSelectedQualifications(values);
        },
    }));

    useEffect(() => {
        props.filterChangedCallback();
    }, [selectedQualifications]);

    return (
        <Box p={2} maxH="220px" overflowY="auto">
            <Flex justify="space-between" align="center" mb={1}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Filter by qualification
                </Text>
                {selectedQualifications.length > 0 && (
                    <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => setSelectedQualifications([])}>
                        Clear
                    </Button>
                )}
            </Flex>
            <CheckboxGroup value={selectedQualifications} onChange={values => setSelectedQualifications(values as string[])}>
                <VStack align="start" spacing={1}>
                    {qualificationOptions.map(option => (
                        <Checkbox key={option} value={option} size="sm">
                            <Text fontSize="sm">{displayQualification(CandidateQualification[option as keyof typeof CandidateQualification])}</Text>
                        </Checkbox>
                    ))}
                </VStack>
            </CheckboxGroup>
        </Box>
    );
});

export default QualificationFilter;
